import { Injectable } from '@angular/core';
import {
  ControlDefinition,
  ControlFactoryService,
} from '../control-factory/control-factory.service';
import { Control, ControlType } from '../../models/controls/control';
import { TranslationsService } from '../translations/translations.service';
import { Controller } from '../../models/controls/controller';
import { RequiredParent } from '../../models/controls/required-parent';

const FieldSeparator = '.';
const ValueSeparator = ',';

@Injectable({
  providedIn: 'root',
})
export class DynamicFormService {
  private controlsByName = new Map<string, Control<any>>();

  private controllerByParentName = new Map<string, Controller[]>();

  constructor(
    private readonly controlFactory: ControlFactoryService,
    private readonly translationsService: TranslationsService
  ) {}

  getForms(contentName: string): Control<any>[][] {
    const controlDefinitions: ControlDefinition[][] = this.translationsService.getContent<
      ControlDefinition[][]
    >(contentName);
    const forms = controlDefinitions.map((definitions) =>
      this.controlFactory.createControls(definitions)
    );
    forms.forEach((controls) =>
      controls.forEach((control) => this.createControllerForRequire(control))
    );
    return forms;
  }

  private createControllerForRequire(control: Control<any>): void {
    this.controlsByName.set(control.name, control);
    if (control.require) {
      this.createController(control);
    }
  }

  private createController(control: Control<any>): void {
    const requirements = this.getRequiredParents(control);
    const controller = new Controller(control, requirements);
    requirements.forEach((requirement) =>
      this.storeController(requirement.name, controller)
    );
  }

  private getRequiredParents(control: Control<any>): RequiredParent[] {
    const requirements: RequiredParent[] = [];
    const require = control.require;
    require.split(ValueSeparator).forEach((requireField) => {
      const [controlName, controlValue] = requireField.split(FieldSeparator);
      const parent = requirements.find(
        (requirement) => requirement.name === controlName
      );
      if (!parent) {
        requirements.push(
          new RequiredParent(controlName, [
            this.parseValue(control.type, controlValue),
          ])
        );
      } else {
        parent.addFieldValue(this.parseValue(control.type, controlValue));
      }
    });
    return requirements;
  }

  private parseValue(controlType: ControlType, value: string): any {
    return controlType === ControlType.checkbox ? value === 'true' : value;
  }

  private storeController(name: string, controller: Controller): void {
    const target = this.controllerByParentName.get(name);
    if (target) {
      target.push(controller);
    } else {
      this.controllerByParentName.set(name, [controller]);
    }
  }

  getControllers(controlName: string): Controller[] {
    return this.controllerByParentName.get(controlName) || [];
  }
}
